import { useEffect, useMemo, useState } from "react";
import platformAPI from "../../../services/platformApi";
import s from "./platformAdmin.module.css";

const CATEGORY_LABEL = {
  core: "Основное",
  finance: "Финансы",
  growth: "Рост",
  team: "Команда",
  limits: "Лимиты",
};

const EMPTY_PLAN = {
  code: "",
  name: "",
  description: "",
  monthly_price_rub: 0,
  yearly_price_rub: "",
  badge: "",
  sort_order: 0,
  is_public: true,
  is_active: true,
  legacy_plan_tier: "",
  features: {},
};

function defaultFeaturesFromCatalog(catalog = []) {
  const out = {};
  for (const f of catalog) {
    out[f.key] = f.type === "number" ? f.default : !!f.default;
  }
  return out;
}

export default function PlatformPlans() {
  const [plans, setPlans] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PLAN);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => {
    Promise.all([platformAPI.getPlans(), platformAPI.getFeaturesCatalog()])
      .then(([plansRes, featRes]) => {
        setPlans(plansRes.plans || []);
        setCatalog(featRes.features || []);
      })
      .catch((e) =>
        setErr(e.response?.data?.error || "Ошибка загрузки тарифов"),
      );
  };

  useEffect(() => {
    load();
  }, []);

  const groupedFeatures = useMemo(() => {
    const groups = {};
    for (const f of catalog) {
      const cat = f.category || "core";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(f);
    }
    return groups;
  }, [catalog]);

  const openCreate = () => {
    setEditing("new");
    setForm({
      ...EMPTY_PLAN,
      features: defaultFeaturesFromCatalog(catalog),
    });
    setMsg("");
    setErr("");
  };

  const openEdit = (plan) => {
    setEditing(plan.id);
    setForm({
      code: plan.code,
      name: plan.name,
      description: plan.description || "",
      monthly_price_rub: plan.monthly_price_rub,
      yearly_price_rub: plan.yearly_price_rub ?? "",
      badge: plan.badge || "",
      sort_order: plan.sort_order || 0,
      is_public: plan.is_public,
      is_active: plan.is_active,
      legacy_plan_tier: plan.legacy_plan_tier || "",
      features: { ...plan.features },
    });
    setMsg("");
    setErr("");
  };

  const setFeature = (key, value) => {
    setForm((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: value },
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    try {
      const body = {
        ...form,
        yearly_price_rub:
          form.yearly_price_rub === "" ? null : Number(form.yearly_price_rub),
        legacy_plan_tier: form.legacy_plan_tier || null,
        badge: form.badge || null,
      };
      if (editing === "new") {
        await platformAPI.createPlan(body);
        setMsg("Тариф создан");
      } else {
        const { code: _c, ...patch } = body;
        await platformAPI.updatePlan(editing, patch);
        setMsg("Тариф сохранён");
      }
      setEditing(null);
      load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Ошибка сохранения");
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Деактивировать тариф?")) return;
    try {
      await platformAPI.updatePlan(id, { is_active: false, is_public: false });
      load();
    } catch (e) {
      setErr(e.response?.data?.error || "Ошибка");
    }
  };

  return (
    <>
      <div className={s.topbar}>
        <div>
          <h1 className={s.title}>Тарифы</h1>
          <p className={s.subtitle}>
            Настройка планов и функционала для клубов BaySport
          </p>
        </div>
        <button type="button" className={`${s.btn} ${s.btnPrimary}`} onClick={openCreate}>
          + Новый тариф
        </button>
      </div>

      {msg ? <div className={s.alertSuccess}>{msg}</div> : null}
      {err ? <div className={s.alertError}>{err}</div> : null}

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Название</th>
              <th>Код</th>
              <th>Цена / мес</th>
              <th>Публичный</th>
              <th>Активен</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => (
              <tr key={plan.id}>
                <td>
                  {plan.name}
                  {plan.badge ? (
                    <span className={s.chip} style={{ marginLeft: 8 }}>
                      {plan.badge}
                    </span>
                  ) : null}
                </td>
                <td>{plan.code}</td>
                <td>{plan.monthly_price_rub} ₽</td>
                <td>{plan.is_public ? "Да" : "Нет"}</td>
                <td>{plan.is_active ? "Да" : "Нет"}</td>
                <td>
                  <button
                    type="button"
                    className={s.btn}
                    onClick={() => openEdit(plan)}
                  >
                    Изменить
                  </button>{" "}
                  {plan.is_active ? (
                    <button
                      type="button"
                      className={`${s.btn} ${s.btnDanger}`}
                      onClick={() => deactivate(plan.id)}
                    >
                      Выкл
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? (
        <form className={s.panel} onSubmit={save} style={{ marginTop: 20 }}>
          <h2 className={s.panelTitle}>
            {editing === "new" ? "Новый тариф" : "Редактирование тарифа"}
          </h2>
          <div className={s.featuresGrid}>
            {editing === "new" ? (
              <div className={s.field}>
                <label>Код (латиница)</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  required
                />
              </div>
            ) : null}
            <div className={s.field}>
              <label>Название</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className={s.field}>
              <label>Цена / мес (₽)</label>
              <input
                type="number"
                min="0"
                value={form.monthly_price_rub}
                onChange={(e) =>
                  setForm({ ...form, monthly_price_rub: e.target.value })
                }
              />
            </div>
            <div className={s.field}>
              <label>Цена / год (₽)</label>
              <input
                type="number"
                min="0"
                value={form.yearly_price_rub}
                onChange={(e) =>
                  setForm({ ...form, yearly_price_rub: e.target.value })
                }
              />
            </div>
            <div className={s.field}>
              <label>Бейдж</label>
              <input
                value={form.badge}
                onChange={(e) => setForm({ ...form, badge: e.target.value })}
                placeholder="Популярный"
              />
            </div>
            <div className={s.field}>
              <label>Порядок</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setForm({ ...form, sort_order: e.target.value })
                }
              />
            </div>
          </div>
          <div className={s.field}>
            <label>Описание</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </div>
          <div className={s.field}>
            <label>
              <input
                type="checkbox"
                checked={form.is_public}
                onChange={(e) =>
                  setForm({ ...form, is_public: e.target.checked })
                }
              />{" "}
              Показывать на лендинге
            </label>
          </div>
          <div className={s.field}>
            <label>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />{" "}
              Активен
            </label>
          </div>

          {Object.entries(groupedFeatures).map(([cat, items]) => (
            <div key={cat} style={{ marginTop: 16 }}>
              <h3 className={s.panelTitle}>{CATEGORY_LABEL[cat] || cat}</h3>
              <div className={s.featuresGrid}>
                {items.map((f) => (
                  <div key={f.key} className={s.featureRow}>
                    <span>{f.name}</span>
                    {f.type === "number" ? (
                      <input
                        type="number"
                        min="0"
                        value={form.features[f.key] ?? f.default}
                        onChange={(e) =>
                          setFeature(f.key, Number(e.target.value))
                        }
                      />
                    ) : (
                      <input
                        type="checkbox"
                        checked={!!form.features[f.key]}
                        onChange={(e) => setFeature(f.key, e.target.checked)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className={s.toolbar}>
            <button type="submit" className={`${s.btn} ${s.btnPrimary}`}>
              Сохранить
            </button>
            <button
              type="button"
              className={s.btn}
              onClick={() => setEditing(null)}
            >
              Отмена
            </button>
          </div>
        </form>
      ) : null}
    </>
  );
}
